import { Modal, Tab, OverlayTrigger, Container, Popover, PopoverContent } from 'react-bootstrap';
import ConditionalWrapper from '../Util/ConditionalWrapper';
import React, { useState, useEffect, createContext, memo, useMemo, useContext } from 'react';
import { useIpaffsModalDetailedData } from './useIpaffsData';
import _ from 'lodash';
import IpaffsModalHeader from './IpaffsModalHeader';
import { getIpaffsCategories } from '../apiCalls';
import { getButtonStyle } from './utils';
import IpaffsModalTabDetailedView from './IpaffsModalTabDetailedView';
import IpaffsModalTabConsolidatedView from './IpaffsModalTabConsolidatedView';
import { GoogleAnalyticsContext } from '../GoogleAnalytics';
import { StoreContext } from '../Store';
import { isLoadStatusSubmitted } from '../../utils/unifiedLoads';
export const IpaffsContext = createContext(null);

const tabs = [{ title: 'Detailed' }, { title: 'Consolidated' }];

const IpaffsModal = ({
  show,
  onHide,
  hideCancel,
  onConfirm,
  itemsRows,
  ipaffsCategories, //store.ipaffsCategories[0]
  setIpaffsCategories, //store.ipaffsCategories[1]
  certificateCount,
  setCertificateCount,
}) => {
  const store = useContext(StoreContext);
  const [currentTab, setCurrentTab] = useState(0);
  const [currentSelectedCategory, setCurrentSelectedCategory] = useState({});
  const [ipaffsRiskCategoriesText, setIpaffsRiskCategoriesText] = useState([]);
  const isLoadSubmitted = isLoadStatusSubmitted(store.loadStatus[0]);

  const data = useIpaffsModalDetailedData(itemsRows, show, ipaffsRiskCategoriesText);
  const ga = useContext(GoogleAnalyticsContext);
  const copyStoreValueToLocal = () => {
    setCurrentSelectedCategory({ ...ipaffsCategories });
  };

  const copyLocalToStoreValue = () => {
    setIpaffsCategories({ ...currentSelectedCategory });
  };

  useEffect(() => {
    if (ga.gaInitialized && show) {
      window?.gtag?.('event', 'Check IPAFFS', {
        event_category: 'Check IPAFFS',
      });
    }
  }, [ga.gaInitialized, show]);

  const allValuesSelectedInDetailedView = useMemo(() => {
    if (
      !ipaffsCategories ||
      typeof ipaffsCategories !== 'object' ||
      !Array.isArray(data.data) ||
      data.data.length === 0
    )
      return false;
    const filtered = Object.values(ipaffsCategories).filter((item) =>
      data.data.map((e) => e.itemId).includes(item.itemId)
    );
    if (!filtered.some((e) => !e.value) && filtered.length) return true;
    return false;
  }, [data, ipaffsCategories]);

  const setActiveTabToConsolidated = () => {
    if (allValuesSelectedInDetailedView) setCurrentTab(1);
    else setCurrentTab(0);
  };

  const consolidatedData = useMemo(() => {
    if (!Object.values(ipaffsCategories).length || !data?.data?.length) return { data: [] };

    const categoryByItemId = Object.values(ipaffsCategories).reduce((map, cat) => {
      map[cat.itemId] = cat;
      return map;
    }, {});

    const consolidatedMap = data.data.reduce((acc, item) => {
      const cat = categoryByItemId[item.itemId];
      const key = [item.origin, cat?.riskCategoryText || 'blank'].join('|');

      if (!acc[key]) {
        acc[key] = {
          key,
          hsCode: item.hsCode,
          hsCodes: [item.hsCode],
          origin: item.origin,
          riskCategory: cat?.riskCategoryText ?? '',
          description: item.description,
          grossWeight: item?.grossWeight ?? 0,
          healthCert: item.healthCert,
          catDesc: item.catDesc,
          ipaffsCode: item.ipaffsCode,
          riskCategoryOptions: item.riskCategoryOptions,
          itemIds: [item.itemId],
          consolidatedIpaffs: cat?.consolidatedIpaffs ?? '',
          ipaffsHSCodes: [item.ipaffsCode],
        };
      } else {
        acc[key].grossWeight += item?.grossWeight ?? 0;
        acc[key].itemIds.push(item.itemId);

        if (item.hsCode && !acc[key].hsCodes.includes(item.hsCode)) {
          acc[key].hsCodes.push(item.hsCode);
        }
        if (item.ipaffsCode && !acc[key].ipaffsHSCodes.includes(item.ipaffsCode)) {
          acc[key].ipaffsHSCodes.push(item.ipaffsCode);
        }
      }

      return acc;
    }, {});
    const returnData = Object.values(consolidatedMap).filter((e) => e.riskCategory);
    return { data: returnData };
  }, [data, ipaffsCategories]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getIpaffsCategories();
        setIpaffsCategories((prevState) => {
          const newValue = { ...prevState };
          for (let [key, value] of Object.entries(newValue)) {
            newValue[key]['riskCategoryText'] =
              response.find((e) => e.id == newValue[key]['riskCategory'])?.categoryOption || '';
          }
          return newValue;
        });

        setIpaffsRiskCategoriesText(response);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    // Set the initial currentSelectedCategory from initialitmsSelectedValue
    if (show) {
      copyStoreValueToLocal();
      setActiveTabToConsolidated();
      ipaffsRiskCategoriesText.length === 0 && fetchData();
    }
  }, [show]);

  const handleSetItemsSelectedValue = (item, value) => {
    setIpaffsCategories((prevState) => ({
      ...prevState,
      [item?.id]: {
        ...prevState[item?.id],
        itemId: item?.itemId,
        value: value,
        riskCategory: value,
        ipaffsHSCode: item?.ipaffsCode,
        riskCategoryName: item.riskCategory,
        riskCategoryText: item.riskCategoryOptions.find((e) => e.id == value)?.data ?? '',
      },
    }));
  };

  const handleSetItemsConsolidatedValue = (item, value) => {
    // Find all ipaffsCategories keys whose itemId is in the consolidated item's itemIds
    const matchingKeys = Object.keys(ipaffsCategories).filter((key) =>
      item?.itemIds?.includes(ipaffsCategories?.[key].itemId)
    );

    if (matchingKeys.length) {
      setIpaffsCategories((prevState) => {
        const newValue = { ...prevState };
        matchingKeys.forEach((e) => {
          newValue[e] = {
            ...newValue[e],
            consolidatedIpaffs: value,
          };
        });
        return newValue;
      });
    }
  };

  const handleCloseModal = () => {
    copyLocalToStoreValue();
    onHide();
  };

  /*this function will get the data from ipaffsCategories and format it to save in the spreadsheet.
   */
  const formatDataToSaveFromDetailedView = () => {
    const formattedData = Object.values(ipaffsCategories)
      .map((item) => {
        if (data.data.map((e) => e.itemId).includes(item.itemId)) {
          return {
            id: item.id,
            itemId: item.itemId,
            value: item.value,
            riskCategoryText: item.riskCategoryText,
            consolidatedIpaffs: item.consolidatedIpaffs,
          };
        }
      })
      .filter((e) => e !== undefined);
    return formattedData;
  };

  const handleSave = (event) => {
    //copy the itemsSelectedValue to ipaffsCategories
    copyStoreValueToLocal();
    const formattedData = formatDataToSaveFromDetailedView();
    onConfirm(formattedData);
    onHide(event);
  };

  const isConsolidatedDisabled = () => {
    if (!allValuesSelectedInDetailedView) {
      return true;
    }
    return false;
  };

  const contextValues = {
    itemsRows,
    handleSetItemsSelectedValue,
    currentSelectedCategory,
    ipaffsCategories,
    ipaffsRiskCategoriesText,
    handleSetItemsConsolidatedValue,
    certificateCount,
    consolidatedData,
    data,
  };

  return (
    <IpaffsContext.Provider value={contextValues}>
      <Modal show={show} onHide={onHide} centered animation={false} dialogClassName="modal-90w">
        <IpaffsModalHeader certificateCount={certificateCount} setCertificateCount={setCertificateCount} />
        <Modal.Body>
          <Container fluid className="pt-4 mt-3">
            <Tab.Container activeKey={currentTab}>
              <div className="px-3 pt-0">
                <div className="tab-header w-100 ipaffs-modal-tabs">
                  {tabs.map((tab, idx) => {
                    const condition = idx == 1 && isConsolidatedDisabled();
                    return (
                      <React.Fragment key={idx}>
                        <ConditionalWrapper
                          condition={condition}
                          wrapper={(children) => (
                            <OverlayTrigger
                              placement="top"
                              trigger={['hover', 'focus']}
                              overlay={
                                <Popover style={{ minWidth: '5rem' }}>
                                  <PopoverContent>
                                    <p>Please fill all the risk categories to view the consolidated view</p>
                                  </PopoverContent>
                                </Popover>
                              }
                            >
                              {children}
                            </OverlayTrigger>
                          )}
                        >
                          <button
                            className={'tab-control text-eoriblue active ipaffs-modal-tab'}
                            style={getButtonStyle(currentTab === idx)}
                            disabled={idx == 1 && isConsolidatedDisabled()}
                            onClick={() => setCurrentTab(idx)}
                          >
                            <span style={getButtonStyle(currentTab === idx)}>{tab.title}</span>
                          </button>
                        </ConditionalWrapper>
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>

              <Tab.Content style={{ paddingTop: '0' }}>
                <Tab.Pane eventKey={0} style={{ paddingTop: '0' }}>
                  <IpaffsModalTabDetailedView readOnly={isLoadSubmitted} />
                </Tab.Pane>
                <Tab.Pane eventKey={1}>
                  <IpaffsModalTabConsolidatedView readOnly={isLoadSubmitted} data={consolidatedData.data} />
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>
          </Container>
        </Modal.Body>
        {!isLoadSubmitted && (
          <Modal.Footer
            class="d-flex flex-row py-3 px-3 gap-3 border-top bg-light justify-content-end align-items-center"
            style={{
              borderBottomLeftRadius: '20px',
              borderBottomRightRadius: '20px',
            }}
          >
            <button
              id="cancel"
              class="blue-button-teams green-btn"
              onClick={() => handleCloseModal()}
              style={{
                display: hideCancel ? 'none' : 'flex',
                borderRadius: '10px',
              }}
            >
              Cancel
            </button>
            <button
              id="confirm"
              class="blue-button-teams"
              onClick={(event) => {
                handleSave(event);
              }}
              style={{ borderRadius: '10px' }}
            >
              Save
            </button>
          </Modal.Footer>
        )}
      </Modal>
    </IpaffsContext.Provider>
  );
};

export default memo(IpaffsModal);
